/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import { useSnackbar } from "notistack";
import * as orgAdmin from "../../api/orgAdmin";
import { ConfirmDeleteDialog } from "../apps/drive/components/DriveDialogs";
import { formatDate } from "../apps/drive/format";
import TeamMembersDialog from "../apps/drive/components/TeamMembersDialog";

/** Every team in the organisation, including ones the admin isn't a member of. */
const AdminTeams = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, teams: [], error: null });
  const [confirming, setConfirming] = useState(null);
  const [managing, setManaging] = useState(null);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      setState({ loading: false, teams: await orgAdmin.listTeams(), error: null });
    } catch (err) {
      setState({ loading: false, teams: [], error: err.message || "Could not load teams." });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

      <Paper variant="outlined">
        {state.teams.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: theme.palette.secondaryText.color }}>
            No team folders in this organisation yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell width={110}>Members</TableCell>
                <TableCell width={110}>Folders</TableCell>
                <TableCell width={130}>Created</TableCell>
                <TableCell width={100} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.teams.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <FolderSharedOutlinedIcon sx={{ fontSize: 18, color: "accent.main" }} />
                      <Box>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t.team_name}</Typography>
                        {t.description && (
                          <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
                            {t.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{t.member_count}</TableCell>
                  <TableCell>{t.folder_count}</TableCell>
                  <TableCell>{formatDate(t.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Manage members">
                      <IconButton size="small" onClick={() => setManaging(t)}>
                        <GroupOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Retire team">
                      <IconButton
                        size="small"
                        sx={{ color: "error.main" }}
                        onClick={() => setConfirming(t)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <ConfirmDeleteDialog
        open={Boolean(confirming)}
        items={confirming ? [{ id: confirming.id, name: confirming.team_name, type: "team" }] : []}
        onConfirm={async () => {
          await orgAdmin.deleteTeam(confirming.id);
          enqueueSnackbar("Team retired. Its folders were kept.", { variant: "success" });
          load();
        }}
        onClose={() => setConfirming(null)}
      />

      {managing && (
        <TeamMembersDialog
          open
          title={`${managing.team_name} members`}
          variant="orgAdmin"
          teamId={managing.id}
          onClose={() => {
            setManaging(null);
            // Member counts in the table are now stale.
            load();
          }}
        />
      )}
    </Box>
  );
};

export default AdminTeams;
